import type { PaywallFeature } from "./constants";

const Bar = ({
  w,
  h = 8,
  className = ""
}: {
  w: number | string;
  h?: number;
  className?: string;
}) => (
  <div
    className={`shrink-0 rounded bg-alpha-100 ${className}`}
    style={{
      width: typeof w === "number" ? `${w}px` : w,
      height: `${h}px`
    }}
  />
);

const Circle = ({ size }: { size: number }) => (
  <div
    className="shrink-0 rounded-full bg-alpha-100"
    style={{ width: `${size}px`, height: `${size}px` }}
  />
);

const TableRow = ({ withCheckbox = false }: { withCheckbox?: boolean }) => (
  <div className="flex items-center gap-6 border-b border-alpha-40 px-6 py-5">
    {withCheckbox ? (
      <div className="h-5 w-5 rounded-full border-[1.5px] border-alpha-100" />
    ) : null}
    <div className="flex flex-1 flex-col gap-2">
      <Bar w={140} h={10} />
      <Bar w={180} h={8} />
    </div>
    <div className="flex w-[140px] items-center gap-2">
      <Circle size={10} />
      <Bar w={80} h={10} />
    </div>
    <div className="flex w-[180px] items-center gap-2 rounded-full bg-alpha-40 px-2 py-1">
      <Circle size={20} />
      <Bar w={100} h={10} />
    </div>
    <Bar w={70} h={10} />
  </div>
);

const TablePage = ({
  rowKey,
  withCheckbox = false
}: {
  rowKey: string;
  withCheckbox?: boolean;
}) => (
  <div className="h-full w-full bg-background-900 px-6 pt-4">
    <div className="overflow-hidden rounded-lg border border-alpha-40 bg-white">
      {Array.from({ length: 8 }).map((_, idx) => (
        <TableRow key={`${rowKey}-${idx}`} withCheckbox={withCheckbox} />
      ))}
    </div>
  </div>
);

const SettingsPage = () => (
  <div className="h-full w-full bg-background-900 p-6">
    <div className="overflow-hidden rounded-lg border border-alpha-40 bg-white">
      {Array.from({ length: 8 }).map((_, idx) => (
        <div
          key={idx}
          className="flex items-center justify-between gap-4 border-b border-alpha-40 px-6 py-5"
        >
          <div className="flex items-center gap-3">
            <Circle size={24} />
            <Bar w={200} h={10} />
          </div>
          <Bar w={80} h={10} />
        </div>
      ))}
    </div>
  </div>
);

const InboxPage = () => (
  <div className="h-full w-full bg-background-900 p-6">
    <div className="overflow-hidden rounded-lg border border-alpha-40 bg-white">
      {Array.from({ length: 6 }).map((_, idx) => (
        <div
          key={idx}
          className="flex items-start gap-4 border-b border-alpha-40 px-6 py-5"
        >
          <Circle size={40} />
          <div className="flex flex-1 flex-col gap-2">
            <Bar w={160} h={10} />
            <Bar w="80%" h={8} />
            <Bar w="60%" h={8} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const CalendarPage = () => (
  <div className="h-full w-full bg-background-900 p-6">
    <div className="grid h-full grid-cols-7 gap-px overflow-hidden rounded-lg border border-alpha-40 bg-alpha-40">
      {Array.from({ length: 7 }).map((_, day) => (
        <div key={day} className="bg-white p-2">
          {Array.from({ length: 4 }).map((_, slot) => (
            <Bar key={slot} w="100%" h={32} className="mb-2 !rounded" />
          ))}
        </div>
      ))}
    </div>
  </div>
);

const ChartsPage = () => (
  <div className="flex h-full w-full flex-col gap-4 bg-background-900 p-6">
    <div className="grid grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, idx) => (
        <div
          key={idx}
          className="flex flex-col gap-3 rounded-lg border border-alpha-40 bg-white p-5"
        >
          <Bar w={90} h={8} />
          <Bar w={60} h={16} />
        </div>
      ))}
    </div>
    <div className="grid flex-1 grid-cols-2 gap-4">
      {Array.from({ length: 2 }).map((_, card) => (
        <div
          key={card}
          className="flex flex-col gap-4 rounded-lg border border-alpha-40 bg-white p-5"
        >
          <Bar w={140} h={10} />
          <div className="flex flex-1 items-end gap-3 pt-4">
            {[72, 56, 88, 40, 64, 30, 48].map((height, idx) => (
              <div
                key={idx}
                className="flex-1 rounded-t bg-alpha-100"
                style={{ height: `${height}%`, minHeight: "24px" }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const CardsPage = () => (
  <div className="h-full w-full bg-background-900 p-6">
    <div className="grid grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, idx) => (
        <div
          key={idx}
          className="flex flex-col gap-3 rounded-lg border border-alpha-40 bg-white p-5"
        >
          <Bar w={120} h={10} />
          <Bar w="90%" h={8} />
          <Bar w="70%" h={8} />
          <div className="mt-2 flex items-center justify-between">
            <Bar w={80} h={8} />
            <Bar w={60} h={8} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

interface PageSkeletonProps {
  feature: PaywallFeature;
}

/**
 * Per-feature page skeleton rendered behind the paywall card.
 * Only the table-content rows render as skeleton; surrounding chrome
 * (filters, headers) is omitted so the blur stays clean around them.
 */
export const PageSkeleton = ({ feature }: PageSkeletonProps) => {
  if (feature === "tasks") return <TablePage rowKey="tasks" withCheckbox />;
  if (feature === "leads") return <TablePage rowKey="leads" />;
  if (feature === "communication") return <InboxPage />;
  if (feature === "showings") return <CalendarPage />;
  if (feature === "lockboxes") return <TablePage rowKey="lockboxes" />;
  if (feature === "phoneNumbers" || feature === "phoneNumbersLimit")
    return <TablePage rowKey="phone-numbers" />;
  if (feature === "workflows" || feature === "workflowsLimit")
    return <TablePage rowKey="workflows" />;
  if (feature === "email") return <InboxPage />;
  if (feature === "analytics") return <ChartsPage />;
  if (feature === "listingSettings" || feature === "listingsPage")
    return <CardsPage />;
  return <SettingsPage />;
};
